import { useEffect, useState, useRef, memo } from "react";
import { FormHeader, RadioGroupField } from "@/components/ui";
import {
	LOAN_PURPOSE_MORTGAGE_BUILD,
	LOAN_PURPOSE_MORTGAGE_INVESTMENT,
	LOAN_PURPOSE_MORTGAGE_OCCUPY,
	LOAN_PURPOSE_MORTGAGE_REFINANCE,
	loan_purpose_mortgage,
} from "@/lib/constants";
import { SuitcaseIcon, HouseLineIcon, HandCoinsIcon, HardHatIcon } from "@phosphor-icons/react";

export interface FinanceFormData {
	loanType: string;
}

export interface FinanceFormErrors {
	loanType: boolean;
}

interface FormDataProps {
	formData: FinanceFormData;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
	formErrors: FinanceFormErrors;
	setFormErrors: React.Dispatch<React.SetStateAction<any>>;
}

// Constants
const RESPONSIVE_BREAKPOINT = 400;

function FinanceForm({ formData, setFormData, formErrors, setFormErrors }: FormDataProps) {
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);
	const [formContainer, setFormContainer] = useState(0);

	const headingRef = useRef<HTMLHeadingElement | null>(null);
	const navDivRef = useRef<HTMLDivElement | null>(null);

	// Handle responsive layout with ResizeObserver
	useEffect(() => {
		const container = navDivRef.current;
		if (!container) return;

		const observer = new ResizeObserver(([entry]) => {
			setIsNavDivSmall(entry.contentRect.width < RESPONSIVE_BREAKPOINT);
		});

		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	// For form container
	useEffect(() => {
		const limiter = 600;

		setIsNavDivSmall(formContainer < limiter);
	}, [formContainer]);

	return (
		<div ref={navDivRef} className="yF-flex yF-flex-col yF-space-y-4">
			<FormHeader
				formType="consultation"
				icon="tag"
				title="Select Finance Option"
				description="Select the finance option you're interested in."
				headingRef={headingRef}
			/>

			{/* Loan Types */}
			<div>
				<RadioGroupField
					id="loanType"
					label="Finance Options"
					value={formData.loanType}
					onChange={(value) => {
						setFormData((prevData: any) => ({
							...prevData,
							loanType: value,
						}));
						setFormErrors((prevErrors: any) => ({
							...prevErrors,
							loanType: false,
						}));
					}}
					options={loan_purpose_mortgage.map((item) => ({
						id: item.option,
						value: item.option,
						label: item.option,
						icon:
							item.value === LOAN_PURPOSE_MORTGAGE_REFINANCE ? (
								<HandCoinsIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_MORTGAGE_INVESTMENT ? (
								<SuitcaseIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_MORTGAGE_OCCUPY ? (
								<HouseLineIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_MORTGAGE_BUILD ? (
								<HardHatIcon size={24} weight="duotone" />
							) : null,
					}))}
					error={formErrors.loanType ? "This field is required" : ""}
					required={false}
					disabled={false}
					className={""}
					gridCols={2}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			</div>
		</div>
	);
}

export default memo(FinanceForm);
